// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonProps } from "@insite/mobius/Button";
import Mail from "@insite/mobius/Icons/Mail";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@store/MobiusOverrides/Button/Button";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import {
    sendRestrictedItemFeedbackEmail,
    SendRestrictedItemFeedbackEmailParams,
} from "@store/Framework/Services/BuyingListContactSupervisorService";
import { useWebsite } from "@store/Framework/Store/Context/ContextHooks";
import Modal, { ModalProps } from "@store/MobiusOverrides/Modal/Modal";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useContext } from "react";
import styled, { css } from "styled-components";

interface BuyingListDeedbackProps {
    isOpen: boolean;
    onConfirmOverride?: (params: SendRestrictedItemFeedbackEmailParams) => Promise<any>;
    handleClose: () => void;
    productName: string;
    productOveride?: ProductModel;
    extendedStyles?: BuyingListFeedbackModalStyles;
    overrideStyles?: BuyingListFeedbackModalStyles;
}

interface BuyingListFeedbackModalStyles {
    modal: ModalProps;
    textArea: TextAreaProps;
    buttonContainer: InjectableCss;
    buttonSubmit: ButtonProps;
    buttonCancel: ButtonProps;
}

const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 480, last: false });

const buyingListFeedbackModalStyles: Partial<BuyingListFeedbackModalStyles> = {
    buttonContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            width: 100%;
            margin-top: 1.25rem;
            padding: 1.25rem;
            gap: 2rem;
            ${mobileQuery} {
                justify-content: center;
            }
        `,
    },
    modal: {
        cssOverrides: {
            modalBody: css`
                ${mobileQuery} {
                    max-height: 75vh !important;
                    max-width: 90vw !important;
                }
            `,
        },
    },
    buttonSubmit: {
        css: css`
            span:nth-child(2) {
                margin-left: 0.75rem;
            }

            svg {
                width: ${pxToRem(22)};
            }
        `,
    },
    buttonCancel: {
        css: css`
            min-width: ${pxToRem(115)};
        `,
    },
    textArea: {
        css: css`
            && {
                margin-top: 1.25rem;
                ${mobileQuery} {
                    margin-top: 5vh;
                    height: 12rem;
                }
            }
        `,
    },
};

const BuyingListFeedbackModal: React.FC<BuyingListDeedbackProps> = ({
    isOpen,
    onConfirmOverride,
    handleClose,
    extendedStyles,
    overrideStyles,
    productOveride,
}) => {
    const styles = useExpandProps(buyingListFeedbackModalStyles, extendedStyles, overrideStyles);
    const [textAreaValue, setTextAreaValue] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const productContext = useContext(ProductContext);
    const product = productOveride || productContext?.product;
    const toaster = useToaster();

    if (!product) {
        return null;
    }
    const productName = product?.productTitle || "";
    const sku = product?.productNumber || "";
    const websiteHostAddress = window.location.origin;
    const productUrl = `${websiteHostAddress}${product.canonicalUrl || ""}`;
    const onConfirm = async (params: SendRestrictedItemFeedbackEmailParams) => {
        try {
            await sendRestrictedItemFeedbackEmail(params);
            toaster.addToast({ body: translate("Email sent successfully"), messageType: "success" });
            handleClose();
        } catch (error) {
            toaster.addToast({ body: translate("Email failed to send"), messageType: "danger" });
        }
    };
    const handleConfirm = async () => {
        const params: SendRestrictedItemFeedbackEmailParams = {
            comment: textAreaValue,
            reportedUrl: productUrl,
            sku,
        };
        setIsLoading(true);
        if (onConfirmOverride) {
            await onConfirmOverride(params);
        } else {
            await onConfirm(params);
            handleClose();
        }
        setIsLoading(false);
    };

    return (
        <Modal
            isOpen={isOpen}
            headline={translate(`Contact your Supervisor about ${productName}`)}
            handleClose={handleClose}
            data-test-selector="buyingListFeedback_modal"
        >
            <LoadingOverlay loading={isLoading} data-test-selector="buyingListFeedback_loadingOverlay">
                <TextArea
                    error={textAreaValue.length > 3000}
                    value={textAreaValue}
                    onChange={e => setTextAreaValue(e.target.value)}
                    placeholder={translate("Please enter comments here")}
                    {...styles.textArea}
                    disabled={isLoading}
                    data-test-selector="buyingListFeedback_textArea"
                />
                <StyledWrapper {...styles.buttonContainer} data-test-selector="buyingListFeedback_buttonContainer">
                    <Button
                        isLoading={isLoading}
                        mergeCss
                        icon={{ src: Mail, position: "right" }}
                        variant="primary"
                        onClick={() => {
                            handleConfirm();
                        }}
                        disabled={textAreaValue.length > 3000}
                        {...styles.buttonSubmit}
                        data-test-selector="buyingListFeedback_sendButton"
                    >
                        {translate("Send")}
                    </Button>

                    <Button
                        isLoading={isLoading}
                        mergeCss
                        variant="secondary"
                        onClick={handleClose}
                        {...styles.buttonCancel}
                        data-test-selector="buyingListFeedback_cancelButton"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </LoadingOverlay>
        </Modal>
    );
};

export default BuyingListFeedbackModal;
